/**
 * Wiring the screens into the shell.
 *
 * main.tsx imports every `features/**\/register.tsx` for its side effects, so
 * this is the one file that knows which screens exist. App.tsx does not, which
 * is the point: adding a screen never touches the shell, and the shell can
 * render with none of them.
 *
 * The active screen lives in the workspace store, because the switcher renders
 * in the header (topBarActions slot) and the screen renders in the main slot,
 * and those are two separate roots as far as the shell is concerned.
 */

import { useState } from 'react';

import { registerShellSlot } from '@/App';
import { AdminView } from '@/features/admin/AdminView';
import { CommandView } from '@/features/command/CommandView';
import { DataView } from '@/features/data/DataView';
import { IdentifyPrompt } from '@/features/data/IdentifyDialog';
import { FleetRail } from '@/features/fleet/FleetRail';
import { FlightsView } from '@/features/flights/FlightsView';
import { HealthView } from '@/features/health/HealthView';
import { PlanningView } from '@/features/planning/PlanningView';
import { SupplyView } from '@/features/supply/SupplyView';
import { TrainingView } from '@/features/tutorial/TrainingView';
import { SCREENS } from '@/features/workspace/screenList';
import { registerScreens, useAvailableScreens, type ScreenEntry } from '@/features/workspace/screens';
import { cn } from '@/lib/utils';
import { useViewport } from '@/mobile/useViewport';
import { useCanAdminister, useHasRole } from '@/stores/authStore';
import { useScreen, useWorkspaceStore } from '@/stores/workspaceStore';

/**
 * The tablet form of the switcher. Eight segments are about 620px, more than
 * three quarters of a 1024px header, so below desktop the active screen shows and the
 * rest open from it.
 */
function ScreenMenu({ available }: { available: ScreenEntry[] }) {
  const active = useScreen();
  const setScreen = useWorkspaceStore((state) => state.setScreen);
  const [open, setOpen] = useState(false);
  const current = available.find((screen) => screen.id === active) ?? available[0];

  return (
    <span className="relative inline-flex shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Screen: ${current?.label ?? ''}. Change screen`}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-[99px] bg-[oklch(1_0_0/0.92)] px-4 py-[9px] text-[12px] font-semibold whitespace-nowrap text-[#0c0c0d] shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
      >
        {current?.label}
        <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <nav
            aria-label="Screens"
            className="glass scroll-thin absolute left-0 top-[calc(100%+10px)] z-20 flex max-h-[calc(100dvh-96px)] min-w-[200px] flex-col gap-0.5 overflow-y-auto rounded-[16px] p-1.5"
          >
            {available.map((screen) => {
              const Icon = screen.icon;
              const isActive = active === screen.id;
              return (
                <button
                  key={screen.id}
                  type="button"
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => {
                    setOpen(false);
                    setScreen(screen.id);
                  }}
                  className="flex min-h-[40px] items-center gap-2.5 rounded-[11px] px-[11px] py-2 text-left text-[12.5px]"
                  style={{ background: isActive ? 'oklch(1 0 0 / 0.1)' : 'transparent' }}
                >
                  <Icon className="size-4 shrink-0" strokeWidth={isActive ? 2.4 : 1.8} />
                  {screen.label}
                  {isActive ? <span className="ml-auto text-[10px]">✓</span> : null}
                </button>
              );
            })}
          </nav>
        </>
      ) : null}
    </span>
  );
}

function ScreenSwitcher() {
  const active = useScreen();
  const setScreen = useWorkspaceStore((state) => state.setScreen);
  const available = useAvailableScreens();
  const tier = useViewport();

  if (tier === 'tablet') return <ScreenMenu available={available} />;

  return (
    <nav
      className="flex shrink-0 items-center gap-1 rounded-[99px] border border-[color:var(--border-nested)] bg-[oklch(1_0_0/0.06)] p-1"
      aria-label="Screens"
    >
      {available.map((screen) => {
        const isActive = active === screen.id;
        return (
          <button
            key={screen.id}
            type="button"
            aria-current={isActive ? 'page' : undefined}
            onClick={() => setScreen(screen.id)}
            className={cn(
              'rounded-[99px] px-4 py-[7px] text-[12px] whitespace-nowrap transition-colors desktop:px-3',
              isActive
                ? 'bg-[oklch(1_0_0/0.92)] font-semibold text-[#0c0c0d] shadow-[0_2px_8px_rgba(0,0,0,0.35)]'
                : 'text-[color:var(--muted-foreground-3)] hover:text-foreground',
            )}
          >
            {screen.label}
          </button>
        );
      })}
    </nav>
  );
}

function MainView() {
  const active = useScreen();
  const isAdmin = useCanAdminister();
  const canPlan = useHasRole('operator');

  // A role change while a screen is open falls back to the map rather than
  // leaving an admin panel on screen for somebody who has just been demoted.
  if (active === 'admin' && !isAdmin) return <CommandView />;
  if ((active === 'planning' || active === 'training') && !canPlan) return <CommandView />;

  switch (active) {
    case 'planning':
      return <PlanningView />;
    case 'flights':
      return <FlightsView />;
    case 'health':
      return <HealthView />;
    case 'supply':
      return <SupplyView />;
    case 'data':
      return <DataView />;
    case 'admin':
      return <AdminView />;
    case 'training':
      return <TrainingView />;
    case 'command':
    default:
      return <CommandView />;
  }
}

registerScreens(SCREENS);
registerShellSlot('fleetRail', () => <FleetRail />);
// The identify prompt rides along with whatever screen is up: it opens for the
// operator who just connected an aircraft, wherever they happen to be.
registerShellSlot('mainView', () => (
  <>
    <MainView />
    <IdentifyPrompt />
  </>
));
registerShellSlot('topBarActions', () => <ScreenSwitcher />);
